import React, { Component, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw, Home, Wifi, WifiOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { errorTracker } from '@/utils/errorTracking';
import { networkMonitor } from '@/utils/networkUtils';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  context?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  isNetworkError: boolean;
  isOnline: boolean;
  retryCount: number;
}

export class EnhancedErrorBoundary extends Component<Props, State> {
  private networkUnsubscribe?: () => void;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      isNetworkError: false,
      isOnline: networkMonitor.getStatus(),
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const isNetworkError = 
      error.message.includes('fetch') ||
      error.message.includes('network') ||
      error.message.includes('NetworkError') ||
      error.message.includes('Failed to fetch');

    return { 
      hasError: true, 
      error,
      isNetworkError
    };
  }

  componentDidMount() {
    this.networkUnsubscribe = networkMonitor.addListener((isOnline) => {
      this.setState({ isOnline });
    });
  }

  componentWillUnmount() {
    if (this.networkUnsubscribe) {
      this.networkUnsubscribe();
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('EnhancedErrorBoundary caught error:', error, errorInfo);
    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);
    
    // Track the error
    errorTracker.logCrash(error, errorInfo);
  }

  handleRetry = () => {
    this.setState(prev => ({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      retryCount: prev.retryCount + 1
    }));
  };

  handleRefresh = () => {
    // Don't auto-refresh to prevent infinite loops
    console.log('Manual refresh required - auto-refresh disabled');
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background via-muted/30 to-background p-4">
          <Card className="max-w-lg mx-auto border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center text-destructive">
                <AlertTriangle className="h-6 w-6 mr-3" />
                {this.state.isNetworkError ? 'Connection Problem' : 'Something went wrong'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant={this.state.isNetworkError ? "default" : "destructive"}>
                {this.state.isNetworkError ? <WifiOff className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                <AlertDescription>
                  {this.state.isNetworkError ? (
                    <div>
                      <p>Unable to connect to our servers. This might be due to:</p>
                      <ul className="list-disc list-inside mt-2 text-sm space-y-1">
                        <li>Poor internet connection</li>
                        <li>Server maintenance</li>
                        <li>Firewall or proxy issues</li>
                      </ul>
                    </div>
                  ) : (
                    <div>
                      The application encountered an unexpected error. We've been notified and are working to fix it.
                      {this.state.error?.message && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm font-medium">Technical details</summary>
                          <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                            {this.state.error.message}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </AlertDescription>
              </Alert>

              {/* Network status indicator */}
              <div className="flex items-center text-sm text-muted-foreground">
                {this.state.isOnline ? (
                  <>
                    <Wifi className="h-4 w-4 mr-2 text-green-500" />
                    Connection status: Online
                  </>
                ) : (
                  <>
                    <WifiOff className="h-4 w-4 mr-2 text-destructive" />
                    Connection status: Offline
                  </>
                )}
              </div>

              {this.state.retryCount > 0 && (
                <div className="text-sm text-muted-foreground">
                  Retry attempts: {this.state.retryCount}
                </div>
              )}
              
              <div className="flex gap-3">
                <Button 
                  onClick={this.handleRetry} 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  disabled={!this.state.isOnline && this.state.isNetworkError}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
                <Link to="/" className="flex-1">
                  <Button variant="secondary" size="sm" className="w-full">
                    <Home className="h-4 w-4 mr-2" />
                    Go Home
                  </Button>
                </Link>
              </div>
              
              <Button 
                onClick={this.handleRefresh} 
                variant="ghost" 
                size="sm" 
                className="w-full"
              >
                Refresh Page
              </Button>

              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm font-medium">Debug Info (Dev Mode)</summary>
                  <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}